import { CommonModule } from '@angular/common';
import { Component, inject, Inject, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import {
  MSAL_GUARD_CONFIG,
  MsalBroadcastService,
  MsalGuardConfiguration,
  MsalModule,
  MsalService,
} from '@azure/msal-angular';
import {
  AuthenticationResult,
  EventMessage,
  EventType,
  InteractionStatus,
  PopupRequest,
  RedirectRequest,
} from '@azure/msal-browser';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { UserActivityService } from './core/services/user-activity/user-activity.service';
import { LayoutComponent } from './shared/components/layout/layout.component';
import { LoaderComponent } from './shared/components/loader/loader.component';
import { CopyDirective } from './shared/directives/copy.directive';
import { AuthService } from './shared/services/auth-service/auth.service';
import { UserService } from './shared/services/user/user.service';
import { AlertService } from './core/services/alert.service';
import { ALERT_MESSAGES } from './core/constants';
import { RoleService } from './shared/services/role-service/role.service';
import { NotificationService } from './shared/services/notification/notification.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  standalone: true,
  imports: [
    CommonModule,
    MsalModule,
    LayoutComponent,
    LoaderComponent,
    CopyDirective,
  ],
})
export class AppComponent implements OnInit, OnDestroy {
  title = 'Nerve Center';
  isLoggedIn: boolean = false;
  isIframe = false;
  loginDisplay = false;
  private isTrackerSet: boolean = false;
  private readonly _destroying$ = new Subject<void>();
  private userActivityService = inject(UserActivityService);
  private customAuthService = inject(AuthService);
  private roleService = inject(RoleService);
  constructor(
    @Inject(MSAL_GUARD_CONFIG) private msalGuardConfig: MsalGuardConfiguration,
    private authService: MsalService,
    private userService: UserService,
    private msalBroadcastService: MsalBroadcastService,
    private router: Router,
    private alertService: AlertService,
    private notificationService: NotificationService,
  ) {}

  ngOnInit(): void {
    this.authService.handleRedirectObservable().subscribe();
    this.isIframe = window !== window.parent && !window.opener; // Remove this line to use Angular Universal
    this.setLoginDisplay();

    this.authService.instance.enableAccountStorageEvents(); // Optional - This will enable ACCOUNT_ADDED and ACCOUNT_REMOVED events emitted when a user logs in or out of another tab or window
    this.msalBroadcastService.msalSubject$
      .pipe(
        filter(
          (msg: EventMessage) =>
            msg.eventType === EventType.ACCOUNT_ADDED ||
            msg.eventType === EventType.ACCOUNT_REMOVED
        )
      )
      .subscribe({
        next: (result: EventMessage) => {
          if (this.authService.instance.getAllAccounts().length === 0) {
            console.log('here');
            window.location.pathname = '/';
          } else {
            this.setLoginDisplay();
          }
        },
        error: (err) => {
          console.error(err);
        },
      });

    this.msalBroadcastService.inProgress$
      .pipe(
        filter(
          (status: InteractionStatus) => status === InteractionStatus.None
        ),
        takeUntil(this._destroying$)
      )
      .subscribe(() => {
        this.setLoginDisplay();
        this.checkAndSetActiveAccount();
      });
  }
  setLoginDisplay() {
    const hasAccounts = this.authService.instance.getAllAccounts().length > 0;
    if (hasAccounts) {
      this.authService.initialize().subscribe({
        next: () => {
          this.customAuthService.getUserRoles().then(() => {
            this.loginDisplay = true;

            if (hasAccounts && !this.isTrackerSet) {
              this.userActivityService.trackPageViews();
              this.isTrackerSet = true;
              this.notificationService.fetchMasterNotifications().subscribe();
              this.userService.fetchCurrentUser().subscribe({
                next: (res) => {
                  this.userService.user = res.data;
                },
                error: (err) => {
                  this.alertService.showAlert(
                    err.message || ALERT_MESSAGES.GENERIC_ERROR,
                    'error'
                  );
                },
              });
            }
          });
        },
      });
    }
  }

  checkAndSetActiveAccount() {
    /**
     * If no active account set but there are accounts signed in, sets first account to active account
     * To use active account set here, subscribe to inProgress$ first in your component
     * Note: Basic usage demonstrated. Your app may require more complicated account selection logic
     */
    let activeAccount = this.authService.instance.getActiveAccount();
    if (
      !activeAccount &&
      this.authService.instance.getAllAccounts().length > 0
    ) {
      let accounts = this.authService.instance.getAllAccounts();
      this.authService.instance.setActiveAccount(accounts[0]);
    }
  }

  loginRedirect() {
    if (this.msalGuardConfig.authRequest) {
      this.authService.loginRedirect({
        ...this.msalGuardConfig.authRequest,
      } as RedirectRequest);
    } else {
      this.authService.loginRedirect();
    }
  }

  loginPopup() {
    if (this.msalGuardConfig.authRequest) {
      this.authService
        .loginPopup({ ...this.msalGuardConfig.authRequest } as PopupRequest)
        .subscribe((response: AuthenticationResult) => {
          this.authService.instance.setActiveAccount(response.account);
        });
    } else {
      this.authService
        .loginPopup()
        .subscribe((response: AuthenticationResult) => {
          this.authService.instance.setActiveAccount(response.account);
        });
    }
  }

  logout(popup?: boolean) {
    if (popup) {
      this.authService.logoutPopup({
        mainWindowRedirectUri: '/',
      });
    } else {
      this.authService.logoutRedirect();
    }
  }

  ngOnDestroy(): void {
    this._destroying$.next(undefined);
    this._destroying$.complete();
  }
}
